import React from "react";
import { Link } from "@/components/common/Link";
import { SIMULATION_PAGE } from "@/lib/knowledge/pages";
import { RelatedLinks } from "@/components/knowledge/RelatedLinks";
import { StatusBadge } from "@/components/knowledge/StatusBadge";
import { Cpu, ArrowRight } from "lucide-react";
export function SimulationTheoryBridge() {
  const connectedLinks = [{
    to: "/KnowledgeLaboratoryPage",
    label: "Knowledge Laboratory",
    why: "Examine whether simulation skepticism can be resolved by externalist or reliabilist epistemologies."
  }, {
    to: "/PhilosophyAsInvestigationPage",
    label: "Philosophy as Investigation",
    why: "Trace the lineage from Descartes's evil demon thought experiment to Bostrom's computational trilemma."
  }, {
    to: "/LogicLaboratoryPage",
    label: "Logic Laboratory",
    why: "Evaluate the formal deductive structure of Bostrom's trilemma and check for hidden invalid conversions."
  }];
  return <section id="simulation-connection" className="w-full scroll-mt-24 min-w-0">
      {/* Simulation Theory Connection Glass Panel */}
      <div className="ql-glass p-6 sm:p-8 rounded-2xl border border-border min-w-0">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 pb-6 border-b border-border/40 min-w-0">
          <div className="space-y-3 min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <span className="ql-eyebrow break-words">METAPHYSICS // EPISTEMOLOGY</span>
              <StatusBadge status="speculative" compact />
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-foreground break-words">
              Connecting to Simulation Theory
            </h2>

            <div className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed space-y-2.5 max-w-3xl break-words">
              <p>
                The question of whether our observable universe could be a computational simulation represents one of the most provocative modern intersections of metaphysics, physics, and radical skepticism.
              </p>
              <p>
                Historically, the simulation argument is a technological reformulation of classical skepticism, directly echoing René Descartes’s 1641 evil demon doubt and Zhuangzi’s butterfly dream.
              </p>
              <p>
                Scientifically, it raises the critical demarcation problem: does a cosmological simulation make unique, falsifiable empirical predictions, or does it remain an unfalsifiable metaphysical hypothesis?
              </p>
              <p>
                Probabilistically, Nick Bostrom’s formal trilemma relies on counting ancestor simulations and base-rate assumptions, demanding rigorous mathematical evaluation rather than speculative credulity.
              </p>
              <p className="text-foreground/90 font-medium">
                To be scientifically and philosophically honest: the simulation hypothesis is neither established empirical fact nor refuted impossibility; it is a live theoretical thought experiment that illuminates the boundaries of human knowledge.
              </p>
            </div>
          </div>

          <div className="shrink-0 flex flex-col gap-3 self-start md:self-center max-w-full">
            <Link to={SIMULATION_PAGE.route} className="ql-neon-button flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-serif font-bold uppercase tracking-wider min-h-[44px] max-w-full text-center">
              <Cpu className="w-4 h-4 shrink-0" />
              <span className="break-words">Explore Simulation Theory</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          </div>
        </div>

        {/* Cross-domain related links */}
        <div className="mt-6">
          <RelatedLinks title="Connected Inquiry Modules" items={connectedLinks} />
        </div>
      </div>
    </section>;
}